export const EMPTY_RUN_STATE = {
    runs: {},
    runOrder: [],
    nodes: {},
    guardrails: {},
    handoffs: [],
    suspensionTokens: {},
    trace: [],
    compactions: [],
    compacting: false,
    waitingOn: [],
    extensions: {},
};
const TERMINAL_NOT_PAUSED = new Set(['completed', 'error', 'aborted']);
/** The `waitingOn` key of a turn waiting on its summary before it starts, which names no node. */
const BEFORE_THE_TURN = '';
const waiting = (waitingOn) => ({ waitingOn, compacting: waitingOn.length > 0 });
/** The wait before the turn ends once its summary is saved or its agent starts anyway. */
const beforeTheTurnEnded = (state) => waiting(state.waitingOn.filter((key) => key !== BEFORE_THE_TURN));
/** Applies one framework part. Persistent parts reconcile in place by their id. */
export function reduceFrameworkPart(state, part) {
    switch (part.kind) {
        case 'run': {
            const run = part.data;
            const isNew = !(run.runId in state.runs);
            let suspensionTokens = state.suspensionTokens;
            // A finished run that is not waiting on an approval can never be resumed, so a stashed token
            // for it would be echoed on the next message by mistake.
            if (TERMINAL_NOT_PAUSED.has(run.status) && run.runId in suspensionTokens) {
                suspensionTokens = Object.fromEntries(Object.entries(suspensionTokens).filter(([id]) => id !== run.runId));
            }
            return {
                ...state,
                runs: { ...state.runs, [run.runId]: run },
                runOrder: isNew ? [...state.runOrder, run.runId] : state.runOrder,
                suspensionTokens,
                // The engine opens the run before it compacts and reports it again only once it ends, and a
                // new turn starts clear even after a client stopped mid-summary and never heard it end.
                ...waiting([]),
            };
        }
        case 'agent':
            return {
                ...state,
                nodes: { ...state.nodes, [part.data.nodeId]: part.data },
                ...beforeTheTurnEnded(state),
            };
        case 'handoff':
            return { ...state, handoffs: [...state.handoffs, part.data] };
        case 'guardrail':
            return {
                ...state,
                guardrails: { ...state.guardrails, [part.data.guardrailRunId]: part.data },
            };
        case 'suspension':
            return {
                ...state,
                suspensionTokens: { ...state.suspensionTokens, [part.data.runId]: part.data.token },
            };
        case 'compaction': {
            const known = state.compactions.some((each) => each.checkpointId === part.data.checkpointId);
            return {
                ...state,
                compactions: known ? state.compactions : [...state.compactions, part.data],
                ...beforeTheTurnEnded(state),
            };
        }
        case 'compacting': {
            const key = part.nodeId ?? BEFORE_THE_TURN;
            const others = state.waitingOn.filter((each) => each !== key);
            return { ...state, ...waiting(part.state === 'running' ? [...others, key] : others) };
        }
    }
}
